/**
 * Integrations Hub — "GDPR" tab (`?tab=gdpr`).
 *
 * Agency-wide (org-level, NOT per brand) control of the gateway's GDPR PII
 * filter: per MCP server a master filter on/off switch plus per-category block
 * toggles. The platform DEFAULT (no explicit setting) is filter ON with ALL
 * categories blocked — the gateway also fails closed to that state when the
 * config is unreachable — so this tab is where an agency deliberately RELAXES
 * redaction per MCP server, never where protection is silently added.
 *
 * Layout follows the Tool Access tab language: header with a refresh action,
 * an explainer banner, then one collapsible card per MCP platform with the
 * master toggle, a blocked-count summary, and the category checkbox grid.
 *
 * RBAC: owner/manager may edit (`canManage`); team_lead sees read-only (the
 * backend write routes are owner/manager). Every mutation round-trips through
 * central-api (`PUT/DELETE /api/v1/agency/gdpr-settings/{platform}`) and the
 * gateway picks the change up within its 60s config cache.
 *
 * Built in the legacy SCSS style — no MUI. Styles live in
 * `styles/pages/control-center/gdprTab.scss` (all class names scoped `gdprTab*`
 * — the SCSS bundle is global, never add bare generic names).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import LoadingSpinner from '../../ui/loadingSpinner/LoadingSpinner';
import TeachingEmptyState from '../../components/emptyState/TeachingEmptyState';
import CustomSVGs from '../../ui/customSVGs/CustomSVGs';
import { chipSVG, reloadArrowsSVG } from '../../lib/allCustomSVGs';
import {
  GdprCategory,
  GdprPlatformSetting,
  getGdprSettings,
  resetGdprSetting,
  updateGdprSetting,
} from '../../lib/api/gdpr/gdpr-api';

interface GdprTabProps {
  /** The agency Bearer token value ('' when absent — the API would 401).
   *  Passed by VALUE so a mid-page session swap re-keys the fetch effect. */
  agencyToken: string;
  /** owner/manager — may toggle/edit. team_lead is view-only (false). */
  canManage: boolean;
  showError: (msg: string) => void;
  showSuccess: (msg: string) => void;
}

const GdprTab = ({
  agencyToken,
  canManage,
  showError,
  showSuccess,
}: GdprTabProps) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [categories, setCategories] = useState<GdprCategory[]>([]);
  const [platforms, setPlatforms] = useState<GdprPlatformSetting[]>([]);
  /** Platform keys with an in-flight mutation (their controls disable). */
  const [busyKeys, setBusyKeys] = useState<Set<string>>(new Set());
  /** Expanded platform cards (category grid visible). */
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // ----------------------- data fetch -----------------------

  const fetchSettings = useCallback(async () => {
    if (!agencyToken) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const res = await getGdprSettings(agencyToken);
      setCategories(res.categories);
      setPlatforms(res.platforms);
    } catch (err) {
      setLoadError(
        err instanceof Error ? err.message : t('GdprLoadFailed')
      );
    } finally {
      setLoading(false);
    }
  }, [agencyToken, t]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Connected platforms first, then alphabetical — the servers an agency
  // actually uses are the ones it will want to tune.
  const sortedPlatforms = useMemo(() => {
    return [...platforms].sort((a, b) => {
      if (a.connected !== b.connected) return a.connected ? -1 : 1;
      return a.label.localeCompare(b.label);
    });
  }, [platforms]);

  // ----------------------- mutations -----------------------

  const setBusy = (key: string, busy: boolean) => {
    setBusyKeys((prev) => {
      const next = new Set(prev);
      if (busy) next.add(key);
      else next.delete(key);
      return next;
    });
  };

  const applyEcho = (
    key: string,
    echo: { enabled: boolean; blocked_categories: string[]; is_customised: boolean; updated_at?: string | null }
  ) => {
    setPlatforms((prev) =>
      prev.map((p) =>
        p.platform_key === key
          ? {
              ...p,
              enabled: echo.enabled,
              blocked_categories: echo.blocked_categories,
              is_customised: echo.is_customised,
              updated_at: echo.updated_at ?? p.updated_at,
            }
          : p
      )
    );
  };

  const saveSetting = async (
    platform: GdprPlatformSetting,
    next: { enabled: boolean; blocked_categories: string[] }
  ) => {
    if (!canManage) return;
    const key = platform.platform_key;
    setBusy(key, true);
    try {
      const echo = await updateGdprSetting(key, next, agencyToken);
      applyEcho(key, echo);
      showSuccess(t('GdprSettingSaved', { platform: platform.label }));
    } catch (err) {
      showError(
        err instanceof Error ? err.message : t('GdprSettingSaveFailed')
      );
    } finally {
      setBusy(key, false);
    }
  };

  const handleMasterToggle = (platform: GdprPlatformSetting) => {
    saveSetting(platform, {
      enabled: !platform.enabled,
      blocked_categories: platform.blocked_categories,
    });
  };

  const handleCategoryToggle = (
    platform: GdprPlatformSetting,
    categoryKey: string
  ) => {
    const blocked = new Set(platform.blocked_categories);
    if (blocked.has(categoryKey)) blocked.delete(categoryKey);
    else blocked.add(categoryKey);
    saveSetting(platform, {
      enabled: platform.enabled,
      blocked_categories: Array.from(blocked),
    });
  };

  const handleReset = async (platform: GdprPlatformSetting) => {
    if (!canManage) return;
    const key = platform.platform_key;
    setBusy(key, true);
    try {
      const echo = await resetGdprSetting(key, agencyToken);
      applyEcho(key, echo);
      showSuccess(t('GdprSettingReset', { platform: platform.label }));
    } catch (err) {
      showError(
        err instanceof Error ? err.message : t('GdprSettingSaveFailed')
      );
    } finally {
      setBusy(key, false);
    }
  };

  const toggleExpanded = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // ----------------------- render helpers -----------------------

  const renderPlatformCard = (platform: GdprPlatformSetting) => {
    const key = platform.platform_key;
    const busy = busyKeys.has(key);
    const isExpanded = expanded.has(key);
    const blockedCount = platform.blocked_categories.length;
    const allBlocked = blockedCount === categories.length;

    return (
      <div className='gdprTabCard' key={key} data-connected={platform.connected ? 'true' : 'false'}>
        <div className='gdprTabCard__header'>
          <button
            type='button'
            className='gdprTabCard__expand'
            aria-expanded={isExpanded}
            onClick={() => toggleExpanded(key)}
          >
            <span
              className='gdprTabCard__caret'
              data-collapsed={isExpanded ? 'false' : 'true'}
            />
            <span className='gdprTabCard__name'>{platform.label}</span>
            {platform.connected && (
              <span className='gdprTabCard__badge gdprTabCard__badge--connected'>
                {typeof platform.connected_brands === 'number'
                  ? t('GdprConnectedOnBrands', {
                      count: platform.connected_brands,
                    })
                  : t('GdprConnectedBadge')}
              </span>
            )}
            <span
              className={`gdprTabCard__badge ${
                platform.is_customised
                  ? 'gdprTabCard__badge--custom'
                  : 'gdprTabCard__badge--default'
              }`}
            >
              {platform.is_customised
                ? t('GdprCustomisedBadge')
                : t('GdprDefaultBadge')}
            </span>
          </button>

          <div className='gdprTabCard__summary'>
            {platform.enabled
              ? allBlocked
                ? t('GdprAllCategoriesBlocked')
                : t('GdprBlockedCount', {
                    blocked: blockedCount,
                    total: categories.length,
                  })
              : t('GdprFilterDisabled')}
          </div>

          <div className='gdprTabCard__actions'>
            {platform.is_customised && canManage && (
              <button
                type='button'
                className='gdprTabCard__reset'
                disabled={busy}
                onClick={() => handleReset(platform)}
              >
                {t('GdprResetToDefault')}
              </button>
            )}
            {/* Master filter switch — scoped class, shared visual language
                with the Tool Access toggle. */}
            <button
              type='button'
              role='switch'
              aria-checked={platform.enabled}
              aria-label={t('GdprMasterToggleAria', {
                platform: platform.label,
              })}
              className='gdprTabToggle'
              data-on={platform.enabled ? 'true' : 'false'}
              data-busy={busy ? 'true' : 'false'}
              disabled={!canManage || busy}
              onClick={() => handleMasterToggle(platform)}
            >
              <span className='gdprTabToggle__knob' />
            </button>
          </div>
        </div>

        {isExpanded && (
          <div className='gdprTabCard__body'>
            {!platform.enabled && (
              <div className='gdprTabCard__disabledNote'>
                {t('GdprFilterDisabledNote')}
              </div>
            )}
            <div
              className='gdprTabCategories'
              data-disabled={platform.enabled ? 'false' : 'true'}
            >
              {categories.map((category) => {
                const blocked = platform.blocked_categories.includes(
                  category.key
                );
                return (
                  <label
                    className='gdprTabCategories__item'
                    key={category.key}
                    data-blocked={blocked ? 'true' : 'false'}
                  >
                    <input
                      type='checkbox'
                      checked={blocked}
                      disabled={!canManage || busy || !platform.enabled}
                      onChange={() =>
                        handleCategoryToggle(platform, category.key)
                      }
                    />
                    <span className='gdprTabCategories__label'>
                      {category.label}
                    </span>
                    <span className='gdprTabCategories__state'>
                      {blocked
                        ? t('GdprCategoryBlocked')
                        : t('GdprCategoryAllowed')}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ----------------------- render -----------------------

  if (loading) {
    return (
      <div className='gdprTab gdprTab--loading'>
        <LoadingSpinner />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className='gdprTab'>
        <TeachingEmptyState
          icon={chipSVG}
          title={t('GdprLoadFailed')}
          body={loadError}
          actionLabel={t('GdprRetry')}
          onAction={fetchSettings}
        />
      </div>
    );
  }

  return (
    <div className='gdprTab'>
      <div className='gdprTab__header'>
        <div className='gdprTab__headerText'>
          <h2>{t('GdprTabTitle')}</h2>
          <p>{t('GdprTabSubtitle')}</p>
          <p className='gdprTab__orgWide'>{t('GdprOrgWideNote')}</p>
        </div>
        <button
          type='button'
          className='gdprTab__refresh'
          onClick={fetchSettings}
          aria-label={t('GdprRefresh')}
        >
          <CustomSVGs svg={reloadArrowsSVG} />
        </button>
      </div>

      <div className='gdprTab__banner'>
        {t('GdprDefaultBanner')}
        {!canManage && (
          <span className='gdprTab__bannerReadOnly'>
            {' '}
            {t('GdprReadOnlyHint')}
          </span>
        )}
      </div>

      {sortedPlatforms.length === 0 ? (
        <TeachingEmptyState
          icon={chipSVG}
          title={t('GdprEmptyTitle')}
          body={t('GdprEmptyBody')}
        />
      ) : (
        <div className='gdprTab__list'>
          {sortedPlatforms.map(renderPlatformCard)}
        </div>
      )}
    </div>
  );
};

export default GdprTab;
